import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';

const PROMPT_TRUNCATE_LENGTH = 300;

const GET_CUSTOM_SPECIALTY = `
  query GetCustomSpecialty($teamId: ID!, $specialtyId: ID!) {
    getCustomSpecialty(teamId: $teamId, specialtyId: $specialtyId) {
      id name description status generatedPrompt createdAt updatedAt
    }
  }
`;

type CustomSpecialtyDetail = {
  id: string;
  name: string;
  description?: string;
  status: string;
  generatedPrompt?: string;
  createdAt?: string;
  updatedAt?: string;
};

type GetCustomSpecialtyResponse = {
  getCustomSpecialty?: CustomSpecialtyDetail | null;
};

export class CustomSpecialtyDrawerState extends StoreNode {
  constructor(store: Store) {
    super(store);
    makeObservable(this);
  }

  readonly window = new WindowState(this.store);

  @observable specialty: CustomSpecialtyDetail | null = null;
  @observable isLoading = false;
  @observable error: string | null = null;
  @observable isPromptExpanded = false;

  @computed
  get truncatedPrompt(): string {
    const prompt = this.specialty?.generatedPrompt;
    if (!prompt) return '';
    if (prompt.length <= PROMPT_TRUNCATE_LENGTH) return prompt;
    return prompt.slice(0, PROMPT_TRUNCATE_LENGTH) + '...';
  }

  @action
  async open(teamId: string, specialtyId: string) {
    this.isLoading = true;
    this.error = null;
    this.specialty = null;
    this.isPromptExpanded = false;
    this.window.open();

    try {
      const [res] = await this.store.apiService.runRequest<GetCustomSpecialtyResponse>(
        GET_CUSTOM_SPECIALTY,
        { teamId, specialtyId }
      );
      runInAction(() => {
        if (res?.getCustomSpecialty) {
          this.specialty = res.getCustomSpecialty;
        } else {
          this.error = 'Custom specialty not found.';
        }
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.error = 'Failed to load custom specialty details.';
        this.isLoading = false;
      });
    }
  }

  @action
  close() {
    this.window.close();
    this.specialty = null;
    this.error = null;
  }

  @action
  togglePromptExpanded() {
    this.isPromptExpanded = !this.isPromptExpanded;
  }
}
